import React from "react";
import { Chip } from "./components/Chip/Chip";
import { useTranslation } from "react-i18next";
import { useFilterContext } from "../../../../../../../../context/FilterContext";
import { useLocation, useNavigate } from "react-router-dom";
import { FilterState } from "../../../../../../interfaces/FilterState";

const FilterChips: React.FC = () => {
  const { t } = useTranslation();
  const { filterState: filters, setFilterState } = useFilterContext();
  const location = useLocation();
  const navigate = useNavigate();

  const applyAndSync = (updater: (prev: FilterState) => FilterState) => {
    setFilterState((prev) => {
      const next = updater(prev);
      updateUrlParams(next);
      return next;
    });
  };

  const updateUrlParams = (filters: FilterState) => {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        const uniqueValues = Array.from(
          value.filter((v, i, arr) => arr.indexOf(v) === i),
        );
        uniqueValues.forEach((v) => v && params.append(key, v));
      } else if (value instanceof Date) {
        params.set(key, value.toISOString());
      } else if (value) {
        params.set(key, value.toString());
      }
    });

    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true, state: { fromFilters: true } },
    );
  };

  const removeFilter = (filterName: keyof typeof filters, value?: string) => {
    applyAndSync((prev) => {
      const updated: FilterState = { ...prev };

      if (filterName === "tags") {
        updated.tags = updated.tags.filter((tag) => tag !== value);
      } else if (filterName === "keywords") {
        updated.keywords = updated.keywords.filter((k) => k !== value);
      } else if (filterName === "country") {
        updated.country = updated.country.filter((c) => c !== value);
      } else if (filterName === "regulator") {
        updated.regulator = updated.regulator.filter((r) => r !== value);
        if (updated.regulator.length === 0) updated.classes = [];
      } else if (filterName === "classes") {
        updated.classes = updated.classes.filter((c) => c !== value);
      } else if (filterName === "tech_product") {
        updated.tech_product = (updated.tech_product ?? []).filter(
          (v) => v !== value,
        );
      } else if (filterName === "tech_section_vat_act") {
        updated.tech_section_vat_act = (
          updated.tech_section_vat_act ?? []
        ).filter((v) => v !== value);
      } else if (filterName === "gazette_number") {
        updated.gazette_number = (updated.gazette_number ?? []).filter(
          (v) => v !== value,
        );
      } else if (filterName === "judges") {
        updated.judges = (updated.judges ?? []).filter((v) => v !== value);
      } else if (filterName === "status") {
        updated.status = (updated.status ?? []).filter((v) => v !== value);
      } else if (filterName === "city") {
        updated.city = (updated.city ?? []).filter((v) => v !== value);
      } else if (filterName === "subject_matter") {
        updated.subject_matter = (updated.subject_matter ?? []).filter(
          (v) => v !== value,
        );
      } else if (filterName === "date_from" && updated.date_to !== null) {
        updated.date_from = updated.date_to;
        updated.date_to = null;
      } else if (
        filterName === "date_issue_from" &&
        updated.date_issue_to !== null
      ) {
        updated.date_issue_from = updated.date_issue_to;
        updated.date_issue_to = null;
      } else {
        (updated as any)[filterName] = null;
      }

      return updated;
    });
  };

  const renderChips = () => {
    const chips: JSX.Element[] = [];

    (Object.keys(filters) as (keyof typeof filters)[]).forEach((filterName) => {
      const filterValue = filters[filterName];

      if (Array.isArray(filterValue) && filterValue.length) {
        filterValue.forEach((item) => {
          chips.push(
            <Chip
              key={`${filterName}:${item}`}
              label={item}
              onDelete={() => removeFilter(filterName, item)}
            />,
          );
        });
      } else if (
        (filterName === "date_from" || filterName === "date_to") &&
        filterValue instanceof Date
      ) {
        chips.push(
          <Chip
            key={`${filterName}:${(filterValue as Date).toISOString()}`}
            label={`${t("filtersSection.dateOfPublication")} ${filterName === "date_from" ? t("filtersSection.from") : t("filtersSection.until")} ${new Date(filterValue).toLocaleDateString()}`}
            onDelete={() => removeFilter(filterName, filterValue?.toString())}
          />,
        );
      } else if (
        (filterName === "date_issue_from" || filterName === "date_issue_to") &&
        filterValue instanceof Date
      ) {
        chips.push(
          <Chip
            key={`${filterName}:${(filterValue as Date).toISOString()}`}
            label={`${t("filtersSection.dateOfIssue")} ${
              filterName === "date_issue_from"
                ? t("filtersSection.from")
                : t("filtersSection.until")
            } ${new Date(filterValue).toLocaleDateString()}`}
            onDelete={() => removeFilter(filterName, filterValue?.toString())}
          />,
        );
      }
    });

    return chips;
  };

  return <div className="flex flex-wrap gap-x-2">{renderChips()}</div>;
};

export { FilterChips };
